import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDeleteApplicantProcess } from "@/hooks";

interface DeleteApplicationDialogProps {
  application: { id: string; applicantName: string } | null;
  onClose: () => void;
}

const DeleteApplicationDialog = ({ application, onClose }: DeleteApplicationDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const { mutate: deleteApplication, isPending } = useDeleteApplicantProcess();

  useEffect(() => {
    if (application) setReason("");
  }, [application]);

  const handleDelete = () => {
    if (!application) return;
    deleteApplication(
      { id: application.id, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(t("processManagement.applicationDeleted"));
          onClose();
        },
        onError: (error) => {
          console.error("Failed to delete application", error);
          toast.error(t("processManagement.applicationDeleteFailed"));
        },
      },
    );
  };

  return (
    <AlertDialog open={!!application} onOpenChange={(open) => { if (!open && !isPending) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("processManagement.deleteApplicationTitle", { name: application?.applicantName })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("processManagement.deleteApplicationDescription")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("processManagement.deleteApplicationReasonPlaceholder")}
          maxLength={500}
          disabled={isPending}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t("processManagement.cancel")}</AlertDialogCancel>
          <Button onClick={handleDelete} disabled={isPending} className="bg-red-500 text-white hover:bg-red-600">
            {isPending ? t("processManagement.deleting") : t("processManagement.delete")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteApplicationDialog;
